import React, { useRef, useEffect, useState } from 'react';
import styles from './Form.module.scss';
import ChevronIcon from 'components/icons/Chevron';

const VIEWPORT_MARGIN = 8;

const Dropdown = ({
  label,
  options,
  selectedOption,
  setSelectedOption,
  isActive,
  setIsActive,
}) => {
  const dropdownRef = useRef(null);
  const openRowRef = useRef(null);
  const submenuRef = useRef(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isActive) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsActive(false);
      }
    }
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, setIsActive]);

  useEffect(() => {
    if (!isActive) setOpenSubmenu(null);
  }, [isActive]);

  // Anchor the flyout to the arrow icon — the row stretches to the full width
  // of the select, so its right edge is not the visual end of the label.
  const anchorFor = (rowEl) => {
    const rowRect = rowEl.getBoundingClientRect();
    const arrow = rowEl.querySelector(`.${styles.form_select_option_arrow}`);
    const left =
      (arrow ? arrow.getBoundingClientRect().right : rowRect.right) + 8;
    return { top: rowRect.top, left };
  };

  const openSubmenuAt = (rowEl, index) => {
    openRowRef.current = rowEl;
    setSubmenuPosition(anchorFor(rowEl));
    setOpenSubmenu(index);
  };

  const closeSubmenu = () => {
    openRowRef.current = null;
    setOpenSubmenu(null);
  };

  // Keep the flyout pinned to its row while the option list or window scrolls,
  // and clamp it so a long sub-list near the bottom edge stays on screen.
  useEffect(() => {
    if (openSubmenu === null) return;
    const reposition = () => {
      const rowEl = openRowRef.current;
      if (!rowEl) return;
      const { top, left } = anchorFor(rowEl);
      const submenuHeight = submenuRef.current
        ? submenuRef.current.offsetHeight
        : 0;
      const maxTop = window.innerHeight - VIEWPORT_MARGIN - submenuHeight;
      const clampedTop = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));
      setSubmenuPosition((prev) =>
        prev.top === clampedTop && prev.left === left
          ? prev
          : { top: clampedTop, left }
      );
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [openSubmenu]);

  return (
    <div
      ref={dropdownRef}
      className={`${styles.form_select} ${styles.form_input} ${
        isActive ? styles.form_select_active : ''
      }`}
      onClick={() => setIsActive(!isActive)}
    >
      <button className={`${styles.form_select_selected}`}>
        <span>
          {selectedOption || (
            <span className={`${styles.form_select_placeholder}`}>{label}</span>
          )}
        </span>
        <ChevronIcon />
      </button>
      <div
        className={`${styles.form_select_list} ${
          isActive ? styles.form_select_list_active : ''
        }`}
      >
        {options.map((rawOption, index) => {
          const option =
            typeof rawOption === 'object' && rawOption !== null
              ? rawOption
              : { label: rawOption };
          const { label: optionLabel, subOptions } = option;

          if (subOptions) {
            const expanded = openSubmenu === index;
            return (
              <div
                key={index}
                onMouseEnter={(e) => openSubmenuAt(e.currentTarget, index)}
                onMouseLeave={closeSubmenu}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && expanded) {
                    closeSubmenu();
                    e.currentTarget.querySelector('button')?.focus();
                  }
                }}
              >
                <button
                  className={`${styles.form_select_option} ${
                    styles.form_select_option_parent
                  } ${expanded ? styles.form_select_option_parent_active : ''}`}
                  aria-haspopup="true"
                  aria-expanded={expanded}
                  onClick={(e) => {
                    e.stopPropagation();
                    openSubmenuAt(e.currentTarget.parentElement, index);
                  }}
                >
                  <span>{optionLabel}</span>
                  <ChevronIcon
                    className={`${styles.form_select_option_arrow}`}
                  />
                </button>
                {expanded && (
                  <div
                    ref={submenuRef}
                    className={`${styles.form_select_submenu}`}
                    style={{
                      top: submenuPosition.top,
                      left: submenuPosition.left,
                    }}
                  >
                    {subOptions.map((subOption, subIndex) => (
                      <button
                        key={subIndex}
                        className={`${styles.form_select_option}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOption(`${optionLabel} - ${subOption}`);
                          setIsActive(false);
                          closeSubmenu();
                        }}
                      >
                        {subOption}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <button
              key={index}
              className={`${styles.form_select_option}`}
              onClick={() => {
                setSelectedOption(optionLabel);
                setIsActive(false);
              }}
            >
              {optionLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Dropdown;
